import { useSearchParams, usePathname } from '@framework/next/navigation';
import DropdownPanelButtons from '@creditflowresearch/uilib/DropdownPanelButtons';
import SearchWrapper from '@/creditflowresearch/ui/common/components/organisms/SearchWrapper';
import Logo from '@/creditflowresearch/ui/common/components/molecules/Logo';
import AskAiButton from '@/creditflowresearch/ui/common/components/molecules/AskAiButton';
import WithBackground from '@creditflowresearch/uilib/WithBackground';
import HeaderBackground, { BackgroundVariant } from '@creditflowresearch/uilib/HeaderBackground';
import { UserProfileTemplate } from '@/creditflowresearch/ui/common/components/templates/UserProfileTemplate';
import ThemeToggle from '@/creditflowresearch/ui/common/components/molecules/ThemeToggle';
import FeatureToggle from '@framework/ui/Organisms/FeatureToggle/Components';
import { ASK_AI, DARK_THEME_ENABLED } from '@creditflowresearch/config/featureToggle';
import { isDarkThemePage } from '@/creditflowresearch/application/helpers/isDarkThemeSupported';
import SearchInputContainer from '@/creditflowresearch/ui/common/components/organisms/HeaderSearchInput';
import Flex from '@creditflowresearch/uilib/Flex';
import Grid from '@creditflowresearch/uilib/Grid';
import GridItem from '@creditflowresearch/uilib/GridItem';
import PanelContent from '@/creditflowresearch/ui/common/components/organisms/PanelContent/PanelContent';
import { getNavLinks, NAV_ITEMS } from '@/creditflowresearch/ui/common/constants/panelData';
import { useEmNavParams } from '@/creditflowresearch/ui/common/hooks/panels/useEmNavParams';
import { useHeaderMenuContext } from '@/creditflowresearch/application/SearchHeaderMenu/HeaderMenuContext';
import LinksPanelButtons from '@creditflowresearch/uilib/LinksPanelButtons';
import useGetHomePageUrl from '@/creditflowresearch/application/hooks/useGetHomePageUrl';
import HeaderLinks from './HeaderLinks';

import styles from './headerLayoutDesktop.module.scss';

interface IHeaderLayoutDesktopProps {
  logo?: React.ReactNode,
  searchComponent?: React.ReactNode,
  userActionButtons?: React.ReactNode,
  menuActionButtons?: React.ReactNode,
  links?: React.ReactNode,
}

const HeaderLayoutDesktop = ({
  logo,
  searchComponent = <SearchInputContainer />,
  userActionButtons = <UserProfileTemplate />,
  menuActionButtons,
  links = <HeaderLinks />,
}: IHeaderLayoutDesktopProps) => {
  const { isSearchResultsOpen } = useHeaderMenuContext();
  const homePageUrl = useGetHomePageUrl();
  const emNavParams = useEmNavParams();
  const asPath = usePathname();
  const searchParams = useSearchParams();
  const markets = searchParams.get('markets');

  // TODO: dark-theme — remove isThemeToggleVisible once all pages support dark theme.
  const isThemeToggleVisible = isDarkThemePage(asPath);

  const getPanelContent = (panelId: string) => <PanelContent panelId={ panelId } variant="desktop" />;

  const defaultMenuActionButtons = (
    <>
      <DropdownPanelButtons
        items={ NAV_ITEMS }
        getPanelContent={ getPanelContent }
        className={ styles.panelButtons }
      />
      <LinksPanelButtons items={ getNavLinks({ markets }, emNavParams) } />
    </>
  );

  return (
    <SearchWrapper>
      <WithBackground
        className={ styles.headerHolder }
        background={ <HeaderBackground variant={ BackgroundVariant.top } /> }
      >
        <Flex
          direction="column"
          gap={ 10 }
          className={ styles.headerContainer }
          dataTest="desktopHeader"
        >
          { /* Group 1: logo, search, user actions */ }
          <Grid templateColumns="auto 1fr auto" gap={ 30 }>
            <GridItem colStart={ 1 }>
              <Flex align="center" justify="flex-start" className={ styles.logoWrapper }>
                { logo || <Logo url={ homePageUrl } /> }
              </Flex>
            </GridItem>

            <GridItem colStart={ 2 }>
              { searchComponent }
            </GridItem>

            <GridItem colStart={ 3 }>
              <Flex justify="flex-end" gap={ 10 }>
                { /* TODO: dark-theme — remove route check and FeatureToggle wrapper */ }
                { isThemeToggleVisible && (
                  <FeatureToggle featureName={ DARK_THEME_ENABLED }>
                    <ThemeToggle />
                  </FeatureToggle>
                ) }
                <FeatureToggle featureName={ ASK_AI }>
                  <AskAiButton />
                </FeatureToggle>
                { userActionButtons }
              </Flex>
            </GridItem>
          </Grid>

          { /* Group 2: menu, links */ }
          <Grid templateColumns="1fr auto" gap={ 30 }>
            <GridItem colStart={ 1 }>
              {
                !isSearchResultsOpen
                && (
                  <Flex align="stretch" justify="flex-start">
                    { menuActionButtons || defaultMenuActionButtons }
                  </Flex>
                )
              }
            </GridItem>

            <GridItem colStart={ 2 }>
              <Flex align="center" justify="flex-end" className={ styles.links }>
                { links }
              </Flex>
            </GridItem>
          </Grid>
        </Flex>
      </WithBackground>
    </SearchWrapper>
  );
};

export default HeaderLayoutDesktop;
